import { Avatar } from '@mui/material';
import { Label, Tabs } from 'flowbite-react';
import React, { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa6';
import { HiPhone } from 'react-icons/hi';
import { MdEmail } from 'react-icons/md';
import { MyCompanyType } from 'types';

interface Props {
	company: MyCompanyType['company'];
}

const CompanyDrawerBody = ({ company }: Props) => {
	const [details, setDetails] = useState<'details' | 'amDetails' | 'recruiterDetails'>('details');

	return (
		<div className="mt-[20px] w-full flex justify-between sm:flex-col sm:justify-start [&>div]:w-full">
			<Tabs.Group
				theme={{
					tablist: {
						tabitem: {
							base: 'flex items-center justify-center p-4 rounded-t-lg text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 focus-visible:ring-4 focus-visible:ring-blue-300 focus-visible:outline-none',
							styles: {
								default: {
									base: 'rounded-t-lg',
									active: {
										on: 'bg-blue-100 text-blue-600 border-b-2 border-blue-600 dark:bg-gray-800 dark:text-[#7777E7]',
										off: 'border-b-2 border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-600 dark:text-gray-400 dark:hover:bg-gray-800  dark:hover:text-gray-300',
									},
								},
								underline: {
									base: 'rounded-t-lg',
									active: {
										on: 'text-blue-600 rounded-t-lg border-b-2 border-blue-600 active dark:text-[#7777E7] dark:border-blue-500',
										off: 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300',
									},
								},
							},
						},
					},
				}}
				color="purplePlaced"
				aria-label="Tabs with icons"
			>
				<Tabs.Item onClick={() => setDetails('details')} active={details === 'details'} title="Details">
					<div className="text-lg font-bold">Company Details</div>
					<div className="m-3" />
					<div className="grid grid-cols-2 gap-2">
						<div className="grow-2">
							<div>
								<Label
									className="text-[#8F90A6] text-[12px] font-inter font-[500]"
									htmlFor="foundedIn"
									value="Founded In"
								/>
							</div>
							<p className="text-[#28293D] text-sm font-[400] font-inter">{company.foundedIn ?? '--'}</p>
						</div>
						<div className="grow-2">
							<div>
								<Label
									className="text-[#8F90A6] text-[12px] font-inter font-[500]"
									htmlFor="headCount"
									value="Head Count"
								/>
							</div>
							<p className="text-[#28293D] text-sm font-[400] font-inter">{company.headCount ?? '--'}</p>
						</div>
						<div className="grow-2">
							<div>
								<Label
									className="text-[#8F90A6] text-[12px] font-inter font-[500]"
									htmlFor="ownership"
									value="Ownership"
								/>
							</div>
							<p className="text-[#28293D] text-sm font-[400] font-inter">{company.ownership ?? '--'}</p>
						</div>
						<div className="grow-2">
							<div>
								<Label
									className="text-[#8F90A6] text-[12px] font-inter font-[500]"
									htmlFor="natureOfBusiness"
									value="Nature of Business"
								/>
							</div>
							<p className="text-[#28293D] text-sm font-[400] font-inter">{company.natureOfBusiness ?? '--'}</p>
						</div>
						<div className="grow-2">
							<div>
								<Label
									className="text-[#8F90A6] text-[12px] font-inter font-[500]"
									htmlFor="headQuarter"
									value="Company Location"
								/>
							</div>
							<p className="text-[#28293D] text-sm font-[400] font-inter">{company.headQuarter ?? '--'}</p>
						</div>
						{company?.industry && (
							<div className="grow-2">
								<div>
									<Label
										className="text-[#8F90A6] text-[12px] font-inter font-[500]"
										htmlFor="industryType"
										value="Industry type"
									/>
								</div>
								<p className="text-[#28293D] text-sm font-[400] font-inter">{company.industry}</p>
							</div>
						)}
					</div>
				</Tabs.Item>
				<Tabs.Item onClick={() => setDetails('amDetails')} active={details === 'amDetails'} title="Account Managers">
					<div className="flex flex-col gap-4">
						{company.accountManagers
							?.filter(e => e?.accountManager && !e?.accountManager?.flags?.isDeletedAccount)
							?.map((am, i) => (
								<div key={am.type} className="flex gap-3 pb-4 border-b border-gray-200 last-of-type:border-b-0">
									<div>
										<Avatar
											sx={{ width: '48px', height: '48px' }}
											src={am.accountManager?.profile?.profileImageUrl || am.accountManager?.profile?.imageUrl}
										/>
									</div>
									<div>
										<h3 className="text-lg text-[#1C1C28] font-[600] font-inter">{am.accountManager?.name}</h3>
										<div>
											<div className="flex mt-1 items-center gap-[2px]">
												<div
													className="flex gap-[4px] cursor-pointer"
													onClick={() => {
														window.open(`tel:${am.accountManager?.phone}`, '_blank');
													}}
												>
													<HiPhone color="#00B7C4" className="cursor-pointer w-[16px] h-[16px]" />
													<p className="font-inter text-[12px] font-[500] leading-[16px] text-[#555770]">
														+91-{am.accountManager?.phone}
													</p>
												</div>
												<FaWhatsapp
													onClick={() => {
														window.open(`https://wa.me/${am.accountManager?.phone}`, '_blank');
													}}
													color="#00B7C4"
													className="cursor-pointer  w-[16px] h-[16px]"
												/>
											</div>

											<div
												className="flex mt-1 gap-[4px] cursor-pointer"
												onClick={() => {
													window.open(`mailto:${am.accountManager.email}`, '_blank');
												}}
											>
												<MdEmail color="#00B7C4" className="cursor-pointer w-[16px] h-[16px]" />
												<p className="font-inter text-[12px] font-[500] leading-[16px] text-[#555770]">
													{am.accountManager.email}
												</p>
											</div>
										</div>
									</div>
								</div>
							))}
					</div>
				</Tabs.Item>
				<Tabs.Item
					onClick={() => setDetails('recruiterDetails')}
					active={details === 'recruiterDetails'}
					title="Recruiters"
				>
					<div className="flex flex-col gap-4">
						{company.recruiter?.map((recruiter, i) => (
							<div key={recruiter?._id} className="flex gap-3 pb-4 border-b border-gray-200 last-of-type:border-b-0">
								<div>
									<Avatar
										sx={{ width: '48px', height: '48px' }}
										src={recruiter?.profile?.profileImageUrl || recruiter?.profile?.imageUrl}
									/>
								</div>
								<div>
									<h3 className="text-lg text-[#1C1C28] font-[600] font-inter">{recruiter?.name}</h3>
									<div>
										<div className="flex mt-1 items-center gap-[2px]">
											<div
												className="flex gap-[4px] cursor-pointer"
												onClick={() => {
													window.open(`tel:${recruiter?.phone}`, '_blank');
												}}
											>
												<HiPhone color="#00B7C4" className="cursor-pointer w-[16px] h-[16px]" />
												<p className="font-inter text-[12px] font-[500] leading-[16px] text-[#555770]">
													+91-{recruiter?.phone}
												</p>
											</div>
											<FaWhatsapp
												onClick={() => {
													window.open(`https://wa.me/${recruiter?.phone}`, '_blank');
												}}
												color="#00B7C4"
												className="cursor-pointer  w-[16px] h-[16px]"
											/>
										</div>

										<div
											className="flex mt-1 gap-[4px] cursor-pointer"
											onClick={() => {
												window.open(`mailto:${recruiter?.email}`, '_blank');
											}}
										>
											<MdEmail color="#00B7C4" className="cursor-pointer w-[16px] h-[16px]" />
											<p className="font-inter text-[12px] font-[500] leading-[16px] text-[#555770]">
												{recruiter?.email}
											</p>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</Tabs.Item>
			</Tabs.Group>
		</div>
	);
};

export default CompanyDrawerBody;
